import React, { useEffect, useRef, useState } from "react";
import { Bishop } from "../../interfaces/bishop";
import { useLocation, useNavigate } from "react-router-dom";
import { getBishopsWithParams } from "../../services/bishopService";
import { Denomination } from "../../interfaces/denomination";
import { getDenominations } from "../../services/denominationService";
import ToolTip from "../../components/component-tools/ToolTip";

const Bishops = () => {
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [allBishops, setAllBishops] = useState<Bishop[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [denominationFilter, setDenominationFilter] = useState<string>("");
  const [appliedDenominationFilter, setAppliedDenominationFilter] =
    useState<string>("");
  const [showFilter, setShowFilter] = useState(false);
  const [denominations, setDenominations] = useState<Denomination[]>([]);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastBishopRef = useRef<HTMLDivElement | null>(null);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      const trimmedSearchTerm = searchTerm.trim();
      setDebouncedSearchTerm(trimmedSearchTerm);

      if (trimmedSearchTerm === "") {
        setPageNumber(1);
        setHasMore(true);
      } else {
        setAllBishops([]);
        setPageNumber(1);
        setHasMore(true);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    const fetchBishops = async () => {
      try {
        setLoading(true);

        const { bishops, totalRecords } = await getBishopsWithParams(
          pageNumber,
          100,
          debouncedSearchTerm,
          appliedDenominationFilter,
        );

        setAllBishops((prevBishops) => {
          const existingIds = new Set(
            prevBishops.map((bishop) => bishop.bishopID),
          );
          const uniqueBishops = bishops.filter(
            (o) => !existingIds.has(o.bishopID),
          );
          const combined = [...prevBishops, ...uniqueBishops];

          if (combined.length >= totalRecords) setHasMore(false);
          return combined;
        });

        const denominationsResponse = await getDenominations();
        setDenominations(denominationsResponse);
      } catch (error) {
        setErrorMessage("Failed to load bishops.");
        console.error("Error fetching bishops:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBishops();
  }, [debouncedSearchTerm, pageNumber, appliedDenominationFilter]);

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.successMessage, location.pathname, navigate]);

  // Infinite scroll observer
  useEffect(() => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPageNumber((prev) => prev + 1);
        }
      },
      { threshold: 1.0 },
    );

    if (lastBishopRef.current) observer.current.observe(lastBishopRef.current);

    return () => observer.current?.disconnect();
  }, [hasMore, loading]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const applyFilter = () => {
    setAppliedDenominationFilter(denominationFilter);
    setAllBishops([]);
    setPageNumber(1);
    setHasMore(true);
    setShowFilter(false);
  };

  const resetFilter = () => {
    setDenominationFilter("");
    setAppliedDenominationFilter("");
    setAllBishops([]);
    setPageNumber(1);
    setHasMore(true);
    setShowFilter(false);
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <h1 style={{ display: "flex" }}>
          All Bishops
          <ToolTip
            tooltipText="Master list of all bishops"
            placement="right"
            className="info-icon"
          />
        </h1>
        <div className="page-items-container">
          <button
            className="btn-funnel"
            onClick={() => setShowFilter(!showFilter)}
          >
            <i
              className={`bi bi-funnel-fill filter-icon ${
                appliedDenominationFilter ? "active-filter-icon" : ""
              }`}
            ></i>
          </button>

          <div className="search-container" style={{ width: "375px" }}>
            <input
              style={{ border: "none", width: "100%", outline: "none" }}
              type="text"
              placeholder="Search By Name/Contact..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm.trim() ? (
              <i
                className="bi bi-x-circle clear-icon"
                onClick={() => setSearchTerm("")}
                style={{ cursor: "pointer" }}
              ></i>
            ) : (
              <i className="bi bi-search search-icon"></i>
            )}
          </div>
          <button
            className="oxbtn btn-new"
            onClick={() => navigate(`/admin/bishops/create-bishop`)}
          >
            <div> New Bishop </div>
            <i className="bi bi-plus-circle-fill plus-icon"></i>
          </button>
        </div>
      </div>

      {/* Filter Popup */}
      {showFilter && (
        <>
          <div
            className="background-mask"
            onClick={() => setShowFilter(false)}
          ></div>
          <div className="filter-popup">
            <div className="close-button-container">
              <button
                className="close-button"
                onClick={() => setShowFilter(false)}
              >
                <i className="bi bi-x-circle"></i>
              </button>
            </div>
            <div className="filter-header">
              <h2>Filter Bishops</h2>
            </div>
            <div className="filter-body">
              <div className="field-stacked" style={{ flex: "0 0 30%" }}>
                <label className="field-label">Denomination:</label>
                <div className="field-input">
                  <select
                    className="form-control"
                    value={denominationFilter}
                    onChange={(e) => setDenominationFilter(e.target.value)}
                  >
                    <option value="">Select a Denomination</option>
                    {denominations.map((denomination) => (
                      <option
                        key={denomination.denominationID}
                        value={denomination.denominationName}
                      >
                        {denomination.denominationName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="filter-actions">
              <button className="smbtn btn-grey" onClick={resetFilter}>
                Reset
              </button>
              <button className="smbtn btn-green" onClick={applyFilter}>
                Apply
              </button>
            </div>
          </div>
        </>
      )}

      {loading && pageNumber === 1 ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="table-wrapper">
            <div className="table-container" style={{ fontSize: "large" }}>
              <table className="table" style={{ cursor: "pointer" }}>
                <thead>
                  <tr>
                    <th style={{ width: "150px" }}>Client Abbreviation</th>
                    <th style={{ width: "250px" }}>Organization Name</th>
                    <th style={{ width: "250px" }}>Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {allBishops.length === 0 ? (
                    <tr>
                      <td colSpan={3} style={{ textAlign: "center" }}>
                        No bishops found.
                      </td>
                    </tr>
                  ) : (
                    allBishops.map((bishop) => (
                      <tr
                        key={bishop.bishopID}
                        onClick={() =>
                          navigate(
                            `/admin/bishops/view-bishop/${bishop.bishopID}`,
                          )
                        }
                      >
                        <td>
                          <u>{bishop.bishopName}</u>
                        </td>
                        <td>{bishop.diocese}</td>
                        <td>{bishop.contact}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div ref={lastBishopRef} style={{ height: "1px" }}></div>
            </div>
          </div>
        </>
      )}
      {successMessage && (
        <div className="alert-message alert alert-success alert-dismissible fade show">
          {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
      {errorMessage && (
        <div
          className="alert-message alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
    </div>
  );
};

export default Bishops;
